import {Box, Button, createStyles, Group, Stack, Title} from '@mantine/core';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import EntryCalendar from 'calendar/EntryCalendar';
import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import EntityStore from 'stores/EntityStore';
import useOnMount from 'ui-library/hooks/useOnMount';
import useResizeObserver from 'ui-library/hooks/useResizeObserver';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import ActivitySection from './homepage/ActivitySection';
import AISection from './homepage/AISection';
import {COLUMN_SPACING, MAX_CONTENT_WIDTH} from './homepage/HomepageConstants';
import HomepagePreviewBanner from './homepage/HomepagePreviewBanner';
import {trackHomepageEvent} from './homepage/HomepageUtils';
import {
  shouldShowAISection,
  shouldShowMyWorkSection,
  shouldShowWelcomeSection,
  shouldShowWorklistsSection,
} from './homepage/HomepageVisibility';
import MyWorkSection from './homepage/MyWorkSection';
import RecentsSection from './homepage/RecentsSection';
import WelcomeSection from './homepage/WelcomeSection';
import WorklistsSection from './homepage/WorklistsSection';

const SINGLE_COLUMN_MODE_WIDTH = 800;

const useCardsStyles = createStyles((theme) => ({
  columnContainer: {
    maxWidth: MAX_CONTENT_WIDTH,
    margin: '0 auto',
  },
  columnContainerWrapper: {
    flexGrow: 1,
    padding: `${theme.spacing.lg} ${COLUMN_SPACING}`,
    backgroundColor: theme.colors.gray[0],
  },
  column: {
    width: `calc(50% - ${COLUMN_SPACING} / 2)`,
  },
}));

const useHomepageStyles = createStyles((theme, {showHeaderShadow}: {showHeaderShadow: boolean}) => ({
  closeIcon: {
    color: theme.colors.gray[6],
    cursor: 'pointer',
    alignSelf: 'flex-start',
    '&&': {
      height: 18,
      width: 18,
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  negativeXsMarginLeft: {
    marginLeft: `-${theme.spacing.xs}`,
  },
  xsMarginRight: {
    marginRight: theme.spacing.sm,
  },
  stickyHeader: {
    padding: theme.spacing.xxl,
    backgroundColor: theme.white,
    borderBottom: `2px solid ${theme.colors.gray[0]}`,
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    transition: 'box-shadow 0.25s ease-in-out',
    boxShadow: showHeaderShadow ? '0 2px 12px rgba(0, 0, 0, 0.1)' : 'none',
  },
}));

const HomepageCards = (): JSX.Element => {
  const {ref, width} = useResizeObserver();
  const {classes} = useCardsStyles();

  const isSmall = width != null && width < SINGLE_COLUMN_MODE_WIDTH;

  if (isSmall) {
    return (
      <div className={classes.columnContainerWrapper} ref={ref}>
        <HomepagePreviewBanner />
        {shouldShowAISection() && <AISection />}
        <RecentsSection />
        {shouldShowMyWorkSection() && <MyWorkSection />}
        {shouldShowWorklistsSection() && <WorklistsSection />}
        {shouldShowWelcomeSection() && <WelcomeSection />}
        <ActivitySection />
      </div>
    );
  }

  return (
    <div className={classes.columnContainerWrapper} ref={ref}>
      <Box maw={`${MAX_CONTENT_WIDTH}px`} m="0 auto">
        <HomepagePreviewBanner />
        {shouldShowAISection() && <AISection />}
        <Group
          position="center"
          spacing={COLUMN_SPACING}
          className={classes.columnContainer}
          align="flex-start"
        >
          <Stack className={classes.column}>
            <RecentsSection />
            {shouldShowWorklistsSection() && <WorklistsSection />}
            {shouldShowWelcomeSection() && <WelcomeSection />}
          </Stack>
          <Stack className={classes.column}>
            {shouldShowMyWorkSection() && <MyWorkSection />}
            <ActivitySection />
          </Stack>
        </Group>
      </Box>
    </div>
  );
};

/** Preference-gated home sidepanel with recents, worklists, my work, and activity sections. */
export default function Homepage(): JSX.Element {
  const [showCalendar, setShowCalendar] = useState<boolean>(false);

  const user = useStoreSelector(
    EntityStore,
    useCallback((store) => store.currentUser(), [])
  );

  useOnMount(() => {
    SidepanelActions.expand();
  });

  const [showHeaderShadow, setShowHeaderShadow] = useState(false);
  const scrollableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollableRef.current == null) {
      return;
    }
    const scrollable = scrollableRef.current;
    const handleScroll = (): void => {
      setShowHeaderShadow(scrollable.scrollTop > 0);
    };
    scrollable.addEventListener('scroll', handleScroll);
    return () => {
      scrollable.removeEventListener('scroll', handleScroll);
    };
  }, [scrollableRef]);

  const {classes, cx} = useHomepageStyles({showHeaderShadow});

  return (
    <div ref={scrollableRef} className={cx(classes.container, 'sidepanel')}>
      <Group position="apart" noWrap={true} align="flex-start" className={classes.stickyHeader}>
        <Group spacing="xl">
          <Title order={3}>Welcome back{user != null ? `, ${user.name}` : ''}</Title>
          <Button
            size="md"
            radius="md"
            // eslint-disable-next-line no-restricted-syntax -- no custom variant matches the design, yet
            variant="light"
            className={classes.negativeXsMarginLeft}
            onClick={() => {
              if (!showCalendar) {
                trackHomepageEvent('calendar_button_clicked');
              }
              setShowCalendar(!showCalendar);
            }}
          >
            {showCalendar ? (
              'Back to home'
            ) : (
              <>
                <BenchlingIcon className={classes.xsMarginRight} icon="calendar" /> My Calendar
              </>
            )}
          </Button>
        </Group>
        <BenchlingIcon
          icon="close"
          altText="Close"
          className={classes.closeIcon}
          {...getActionButtonRoleProps({
            onPress: () => {
              trackHomepageEvent('close_button_clicked');
              SidepanelActions.close();
            },
          })}
        />
      </Group>
      {/* Inexplicably, EntryCalendar has only left and top margin */}
      {showCalendar ? (
        <div className="u-spacing-right">
          <EntryCalendar />
        </div>
      ) : (
        <HomepageCards />
      )}
    </div>
  );
}
